import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	OnInit,
	Output,
} from '@angular/core';
import { openClose } from '@core/animations/animations';
import { getInitials } from '@core/util/string';
import { AlgoliaPersonClient } from '@fnModels/algolia/client';
import { AlgoliaMatter } from '@fnModels/algolia/matter';
import { BusinessUser } from '@models/businesses';
import { UserMatterViewHistory } from '@models/userViewHistory';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import {
	ClientSearchDispatch,
	ClientSearchState,
} from '@state/algolia-search/clientSearch';
import {
	MatterSearchDispatch,
	MatterSearchState,
} from '@state/algolia-search/matter-search/matter-search.index';
import { combineLatest, Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { RecentlyViewedSelector } from '@state/recently-viewed/recently-viewed.selector';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { MultipurposeNavigationService } from '@services/nav/multipurpose-navigation.service';
import { BaseComponent } from '@core/base.component';
import { TestHooks } from './global-search.hooks';
import { SearchType } from 'src/app/public/leap/leap-api.service';

interface VM {
	recentlyViewed: UserMatterViewHistory[];
	searchValue: string;
	searching: boolean;
	showHistory: boolean;
	searchTime: number;
}

const defaultVM: VM = {
	recentlyViewed: [],
	searchValue: null,
	searching: false,
	showHistory: true,
	searchTime: 0,
};
@UntilDestroy()
@Component({
	selector: 'app-global-search',
	templateUrl: './global-search.component.html',
	styleUrls: ['./global-search.component.scss'],
	animations: [openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class GlobalSearchComponent extends BaseComponent<VM> implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public history = [];

	@ViewSelectSnapshot(ClientSearchState.list) clients: AlgoliaPersonClient[];
	@ViewSelectSnapshot(MatterSearchState.getList) matters: Observable<
		AlgoliaMatter[]
	>;
	@ViewSelectSnapshot(RecentlyViewedSelector.getAll)
	_recentlyViewed: UserMatterViewHistory[];
	@ViewSelectSnapshot(BusinessUsersState.getClerks) clerks: BusinessUser[];
	@ViewSelectSnapshot(BusinessUsersState.getLawyers) lawyers: BusinessUser[];

	@Select(ClientSearchState.searching) searchingClients$: Observable<boolean>;
	@Select(MatterSearchState.getSearching)
	searchingMatters$: Observable<boolean>;

	@Output() onClose: EventEmitter<void> = new EventEmitter<void>();

	constructor(
		private clientSearchDispatch: ClientSearchDispatch,
		private matterSearchDispatch: MatterSearchDispatch,
		private multipurposeNavigationService: MultipurposeNavigationService,
		private analytics: AnalyticsService,
		private drawerService: DrawerService,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.clientSearchDispatch.clearResults();
		this.matterSearchDispatch.clearResults();

		this.VM = {
			recentlyViewed: this._recentlyViewed.map(
				(file: UserMatterViewHistory) => {
					const lawyer = this.lawyers.find(
						(_lawyer) => _lawyer.id === file?.assignedLawyer?.id,
					);
					const clerk = this.clerks.find(
						(_clerk) => _clerk.id === file?.assignedClerk?.id,
					);

					return {
						...file,
						assignedLawyer: {
							...file.assignedLawyer,
							initials: lawyer
								? lawyer.initials ||
									getInitials(`${lawyer.firstName} ${lawyer.lastName}`)
								: '',
						},
						assignedClerk: {
							...file.assignedClerk,
							initials: clerk
								? clerk.initials ||
									getInitials(`${clerk.firstName} ${clerk.lastName}`)
								: '',
						},
					};
				},
			),
		};

		combineLatest([this.searchingClients$, this.searchingMatters$])
			.pipe(untilDestroyed(this))
			.subscribe(([_searchClients, _searchMatters]) => {
				if (!(_searchClients || _searchMatters)) {
					const currTime = new Date().getTime();
					if (currTime < this.VM.searchTime + 1000) {
						setTimeout(
							() => {
								this.VM = {
									searching: false,
								};
							},
							this.VM.searchTime + 1000 - currTime,
						);
					} else {
						this.VM = {
							searching: false,
						};
					}
				}
			});
	}

	trackByMatter(index: number, item: UserMatterViewHistory) {
		return item.id;
	}

	trackByAlgoliaMatter(index: number, item: AlgoliaMatter) {
		return item.id;
	}

	trackByClient(index: number, item: AlgoliaPersonClient) {
		return item.id;
	}

	search(searchValue: string) {
		if (searchValue?.length > 1) {
			this.VM = {
				searchTime: new Date().getTime(),
				showHistory: false,
				searching: true,
			};

			if (searchValue?.length > 0 && searchValue[0] === '#') {
				this.clientSearchDispatch.clearResults();
				const _searchValue = searchValue.substring(1);
				this.matterSearchDispatch.search(_searchValue, {
					filterByNumber: true,
				});
			} else {
				this.clientSearchDispatch.search(
					searchValue,
					SearchType.CardsAndPeople,
				);
				this.matterSearchDispatch.search(searchValue);
			}
		} else {
			this.VM = {
				showHistory: true,
			};
		}
	}

	goToClient(clientId: string) {
		this.drawerService.openDrawer(GlobalProfileComponent, {
			title: 'Global Profile',
			data: { clientId },
		});
	}

	goToMatter(matterId: string, isRecentlyViewed: boolean) {
		if (isRecentlyViewed) {
			this.analytics.logEvent(
				AnalyticsEvent.GLOBAL_SEARCH_RECENTLY_VIEWED_MATTER_CLICK,
				{ matterId: matterId },
			);
		} else {
			this.analytics.logEvent(
				AnalyticsEvent.GLOBAL_SEARCH_RESULTS_MATTER_CLICK,
				{ matterId: matterId },
			);
		}
		this.multipurposeNavigationService.goToMatter(matterId);
		this.onClose.emit();
	}

	stopProp($event) {
		$event.stopPropagation();
	}
}
